export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Last updated: January 2024
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you and Innovative Digital Scaling ("Company," "we," "us," or "our") concerning your access to and use of our website and services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using our services, you agree to be bound by these Terms. If you do not agree with these Terms, you must not access or use our services.
              </p>
            </div>

            {/* Services */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Our Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We provide comprehensive digital solutions including:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                <li>Digital marketing services (SEO, PPC, content marketing, social media)</li>
                <li>Cybersecurity solutions</li>
                <li>AI integration and automation</li>
                <li>VoIP systems and communication solutions</li>
                <li>Reputation management</li>
                <li>Finance and payment solutions</li>
                <li>Web development and design</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify, suspend, or discontinue any part of our services at any time without prior notice.
              </p>
            </div>

            {/* User Accounts */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">3. User Accounts</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you create an account with us, you must provide accurate, complete, and current information. You are responsible for:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Ensuring your account information remains accurate and up-to-date</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to suspend or terminate your account if any information provided is inaccurate, false, or violates these Terms.
              </p>
            </div>

            {/* Acceptable Use */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Acceptable Use Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to use our services to:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the intellectual property rights of others</li>
                <li>Transmit harmful, offensive, or inappropriate content</li>
                <li>Engage in fraudulent or deceptive practices</li>
                <li>Distribute malware, viruses, or other harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt our services or servers</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Collect or harvest personal information without consent</li>
                <li>Use automated systems to access our services without permission</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Intellectual Property Rights</h2>
              
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">5.1 Our Content</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content, features, and functionality on our website, including but not limited to text, graphics, logos, images, software, and design, are owned by us or our licensors and are protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">5.2 Your Content</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                By submitting content to our services, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content for the purpose of providing our services.
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">5.3 Work Product</h3>
              <p className="text-gray-700 leading-relaxed">
                Upon full payment for services rendered, you will own the final deliverables created specifically for you. We retain the right to use general methodologies, processes, and techniques developed during the project.
              </p>
            </div>

            {/* Payment Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Payment and Billing</h2>
              
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">6.1 Fees</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to pay all fees associated with the services you purchase. All fees are non-refundable unless otherwise stated in writing.
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">6.2 Payment Methods</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We accept various payment methods as indicated on our website. You authorize us to charge your chosen payment method for all fees incurred.
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">6.3 Late Payments</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Late payments may result in suspension of services and may incur additional fees. We reserve the right to charge interest on overdue amounts at the rate of 1.5% per month or the maximum rate permitted by law.
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">6.4 Refunds</h3>
              <p className="text-gray-700 leading-relaxed">
                Refund requests must be submitted in writing within 14 days of service delivery. Refunds are evaluated on a case-by-case basis and are not guaranteed.
              </p>
            </div>

            {/* Service Level Agreement */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Service Level Agreement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We strive to provide reliable and high-quality services. However, we do not guarantee:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                <li>Uninterrupted or error-free service</li>
                <li>Specific results or outcomes from our services</li>
                <li>That our services will meet all your requirements</li>
                <li>That defects will be corrected immediately</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We will make reasonable efforts to maintain service availability and address any issues promptly.
              </p>
            </div>

            {/* Confidentiality */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Both parties agree to maintain the confidentiality of any proprietary or confidential information disclosed during the course of our business relationship. This obligation survives the termination of services.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by law, we shall not be liable for:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Service interruptions or delays</li>
                <li>Errors or inaccuracies in content</li>
                <li>Unauthorized access to your data</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Our total liability shall not exceed the amount paid by you for the services in the 12 months preceding the claim.
              </p>
            </div>

            {/* Indemnification */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to indemnify and hold harmless Innovative Digital Scaling and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising from:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                <li>Your use of our services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Content you submit or transmit through our services</li>
              </ul>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may terminate or suspend your access to our services immediately, without prior notice, for any reason, including:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                <li>Breach of these Terms</li>
                <li>Non-payment of fees</li>
                <li>Fraudulent or illegal activity</li>
                <li>At our sole discretion</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Upon termination, your right to use our services will immediately cease. All provisions that should survive termination shall remain in effect.
              </p>
            </div>

            {/* Dispute Resolution */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">12. Dispute Resolution</h2>
              
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">12.1 Informal Resolution</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                In the event of any dispute, both parties agree to first attempt to resolve the matter informally by contacting us at <a href="mailto:viralmentemarket@gmail.com" className="text-blue-600 hover:text-blue-700 cursor-pointer">viralmentemarket@gmail.com</a>
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">12.2 Arbitration</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                If informal resolution fails, any dispute shall be resolved through binding arbitration in accordance with applicable arbitration rules. The arbitration shall be conducted in English.
              </p>

              <h3 className="text-2xl font-semibold text-gray-900 mb-4">12.3 Class Action Waiver</h3>
              <p className="text-gray-700 leading-relaxed">
                You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
              </p>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">13. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates, without regard to its conflict of law provisions.
              </p>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">14. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of our services after such modifications constitutes acceptance of the updated Terms.
              </p>
            </div>

            {/* Severability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">15. Severability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </div>

            {/* Entire Agreement */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">16. Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms, together with our Privacy Policy and any other legal notices published by us, constitute the entire agreement between you and Innovative Digital Scaling concerning your use of our services.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">17. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:viralmentemarket@gmail.com" className="text-blue-600 hover:text-blue-700 cursor-pointer">viralmentemarket@gmail.com</a>
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> <a href="tel:+971508655076" className="text-blue-600 hover:text-blue-700 cursor-pointer">+971 50 865 5076</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
